import { ErrorBoundary, LoadingSpinner, useI18n } from "@rc-acs/shared/ui";
import { Suspense, useCallback, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

/**
 * Route-to-i18n key map for page titles in header.
 * Specific sub-routes (e.g. /admin/billing/plans) MUST precede their parent
 * prefix (/admin/billing) so getRouteKey prefix-matching resolves specific sub-paths first.
 */
const routeKeyMap: Record<string, string> = {
  "/": "route.dashboard",
  "/engines": "route.engineRegistry",
  "/engineering/tr069/virtual-params": "route.engVirtualParams",
  "/engineering/tr069/provisions": "route.engProvisions",
  "/engineering/tr069/presets": "route.engPresets",
  "/engineering/tr069/cwmp-configs": "route.engCwmpConfigs",
  "/engineering/tr069/files": "route.engFiles",
  "/engineering/tr369/controllers": "route.engTr369Controllers",
  "/engineering/devices/cpe-models": "route.engCpeModels",
  "/engineering/devices/vendor-mappings": "route.engVendorMappings",
  "/engineering/devices/firmware": "route.engFirmware",
  "/engineering/olt": "route.engOlt",
  "/engineering/dynamic-vars": "route.engDynamicVars",
  "/admin/users": "route.adminUsers",
  "/admin/settings": "route.adminSettings",
  "/admin/backups": "route.adminBackups",
  "/admin/billing/plans": "route.adminBillingPlans",
  "/admin/billing/clients": "route.adminBillingClients",
  "/admin/billing/contracts": "route.adminBillingContracts",
  "/admin/billing": "route.adminBilling",
};

function getRouteKey(pathname: string): string | undefined {
  if (routeKeyMap[pathname]) return routeKeyMap[pathname];

  for (const [route, key] of Object.entries(routeKeyMap)) {
    if (route !== "/" && pathname.startsWith(`${route}/`)) {
      return key;
    }
  }

  return undefined;
}

export function CoreLayout() {
  const location = useLocation();
  const { t } = useI18n();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const title = useMemo(() => {
    const key = getRouteKey(location.pathname);
    return key ? t(key) : t("route.defaultTitle");
  }, [location.pathname, t]);

  const handleMobileClose = useCallback(() => setMobileOpen(false), []);
  const handleMenuToggle = useCallback(() => setMobileOpen((prev) => !prev), []);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={handleMobileClose}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar title={title} onMenuToggle={handleMenuToggle} />
        <main className="flex-1 overflow-y-auto p-6">
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner className="h-full min-h-[200px]" />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
